import { businessEntityKeys, queryClient } from '@commenda/api';
import { Button, FeaturedIcon, Icons, useToggle } from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { copyToClipboard, getPrintableAddress } from '@commenda/utils';
import { EmptyStateV2 } from '../../../../../components/empty-state/EmptyStateV2';
import { useNotification } from '../../../../../hooks/useNotification';
import { DataPoint } from '../../../../common/DataPoint';
import RegisteredAgentModal from './RegisteredAgentModal';

export const RegisteredAgent = ({
  businessEntity,
  onSuccess,
  hideDescription = false,
  isEditable = false,
  disabled = false,
  hideCopyIconForFields = {
    name: false,
    address: false,
  },
}: {
  businessEntity: IBusinessEntity;
  onSuccess?: () => void;
  hideDescription?: boolean;
  isEditable?: boolean;
  hideCopyIconForFields?: {
    name?: boolean;
    address?: boolean;
  };
  disabled?: boolean;
}) => {
  const { setNotification } = useNotification();
  const registeredAgentModalVisibility = useToggle();

  const { registeredAgent } = businessEntity.generalCorporationInfo;

  return (
    <>
      <RegisteredAgentModal
        isOpen={registeredAgentModalVisibility.visible}
        onClose={registeredAgentModalVisibility.hide}
        businessEntityId={businessEntity.id}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: [
              businessEntityKeys.FETCH_BUSINESS_ENTITY,
              +businessEntity.id,
            ],
          });
          if (onSuccess) onSuccess();
        }}
        registeredAgent={registeredAgent}
      />

      <div className="flex flex-col gap-3xl">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-xs">
            <h3 className="txt-large-bold">Registered Agent</h3>
            {!hideDescription && (
              <p className="txt-small-regular text-text-tertiary max-w-xl">
                Every entity must appoint a registered agent to receive legal,
                tax, and government correspondence on its behalf.
              </p>
            )}
          </div>
          {isEditable && !disabled ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={registeredAgentModalVisibility.show}
            >
              {!registeredAgent ? <Icons.Plus className="size-4" /> : null}
              <span>{registeredAgent ? 'Edit' : 'Add'}</span>
            </Button>
          ) : null}
        </div>
        {!registeredAgent ? (
          <EmptyStateV2
            title="No registered agent found"
            description="Click “add” to add registered agent information."
            icon={<FeaturedIcon icon={<Icons.SearchLg />} />}
          />
        ) : (
          <div className="rounded-xl overflow-clip divide-y divide-border-primary">
            <DataPoint
              title="Name"
              disabled={disabled}
              value={registeredAgent.name}
              onCopy={
                !hideCopyIconForFields.name
                  ? () => {
                      copyToClipboard(registeredAgent.name);
                      setNotification({
                        show: true,
                        valid: true,
                        title: 'Copied to clipboard',
                        message: 'Name of registered agent copied to clipboard',
                      });
                    }
                  : undefined
              }
            />
            <DataPoint
              title="Address"
              disabled={disabled}
              value={getPrintableAddress(registeredAgent.address)}
              onCopy={
                !hideCopyIconForFields.address
                  ? () => {
                      copyToClipboard(
                        getPrintableAddress(registeredAgent.address)
                      );
                      setNotification({
                        show: true,
                        valid: true,
                        title: 'Copied to clipboard',
                        message:
                          'Address of registered agent copied to clipboard',
                      });
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </>
  );
};
